/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

import { installLinkClickTracking } from '@aglyn/aglyn/app-utils/analytics-link-clicks'
import dynamic from 'next/dynamic'
import {
  installUtmForwarding,
  setUtmForwardingConsent,
} from '@aglyn/aglyn/app-utils/utm-forwarding'
import { setUtmTouchConsent } from '@aglyn/aglyn/app-utils/utm-touch'
import { setPageFirstTouchStorage } from '@aglyn/shared-util-first-touch/first-touch-page'
import { installWebVitalsReporting } from '@aglyn/aglyn/app-utils/web-vitals-rum'
import { analyticsMayEmit } from '@aglyn/aglyn/app-utils/analytics-environment'
import { usePageIdle } from '@aglyn/aglyn/app-utils/page-idle'
import {
  analyticsProviders,
  loadAnalyticsProviders,
  subscribeAnalyticsProviders,
  type AnalyticsProvider,
  type AnalyticsTagMount,
} from '@aglyn/aglyn/app-utils/analytics-provider'
import {
  analyticsBeaconMaySend,
  sendAnalyticsBeacon,
} from '@aglyn/aglyn/app-utils/analytics-beacon'
import {
  advertisingGrantedByRecord,
  hostAsksAboutAdvertising,
  hostConfiguresAnalyticsTag,
  hostConsentRequired,
  isAnalyticsAllowed,
  type VisitorConsentHost,
} from '@aglyn/aglyn/app-utils/visitor-consent'
import AdvertisingTags from './advertising-tags'
import { ANALYTICS_PROVIDER_LOADERS } from '../../../../utils/plugins.analytics.generated'

/**
 * The consent surfaces, in a chunk of their own.
 *
 * `ssr: false` is what this component already does by hand: `consent.ready`
 * starts false so the server and the first client render agree that nothing
 * is drawn, which is what keeps the ISR-cached HTML from varying by region
 * (AGL-1498). Deferring the module to match costs the page nothing it was
 * rendering and takes MUI's `Dialog`, `Switch`, `FormControlLabel` and
 * `SwitchBase` — the preferences dialog, which opens on a click that most
 * visits never make — out of first paint on every published screen.
 *
 * The console mounts the same component directly, under its region simulator
 * and its document preview, and is untouched by this.
 *
 * The deferred module is `./consent-banner-chunk`, a relative re-export, and
 * not the core specifier it holds: an `import()` of `@aglyn/aglyn/...`
 * registers a dynamic nx edge on the whole `tenant → aglyn` pair, which
 * forbids every STATIC import of core across the app. That file's docblock
 * carries the reasoning.
 */
const ConsentBannerUi = dynamic(() => import('./consent-banner-chunk'), {
  ssr: false,
})

import Script from 'next/script'
import {
  Fragment,
  useMemo,
  useSyncExternalStore,
  type ReactElement,
} from 'react'
import { primeVisitorConsent, useVisitorConsent } from './use-visitor-consent'
import { claimDailyVisit } from './visit-claim'
import {
  configureSiteJourney,
  hostRecordsJourneys,
} from '@aglyn/aglyn/app-utils/site-journey'

/** What the server renders, and a page with no adapter loaded yet. */
const NO_PROVIDERS: readonly AnalyticsProvider[] = Object.freeze([])
const NO_MOUNTS: readonly AnalyticsTagMount[] = Object.freeze([])
const noProviders = () => NO_PROVIDERS

/** A provider's tags, or none if it throws — analytics never breaks the page. */
function mountsOf(
  provider: AnalyticsProvider,
  ...args: Parameters<AnalyticsProvider['mounts']>
): readonly AnalyticsTagMount[] {
  try {
    return provider.mounts(...args)
  } catch {
    return NO_MOUNTS
  }
}

/**
 * Pageviews this page load has already DECIDED, keyed by host and path.
 * Module scope, so it survives the re-renders and remounts a page does on its
 * own — `/api/analytics/collect` is a metered-billing input and must be told
 * about a pageview once.
 *
 * A pageview the beacon gate refused is recorded here too. The gate reads
 * `localStorage` and the answer cannot change within one pageview, so
 * re-deciding on every render would buy nothing and cost a storage read each
 * time.
 */
const beaconed = new Set<string>()

/**
 * Where the console lives, for the AGL-1731 campaign hop. Configured rather
 * than written, because a self-host install points this somewhere else
 * entirely — and because it is the allowlist: it is the ONLY origin whose
 * links get a campaign put on them, so a third-party link can never be handed
 * our marketing labels.
 */
const CONSOLE_ORIGIN =
  process.env.NEXT_PUBLIC_CONSOLE_URL ?? 'https://app.aglyn.com'

/**
 * Send the pageview beacon (AGL-82), NOT from an effect (AGL-1550).
 *
 * An effect only runs if React commits, and a page that renders but never
 * commits is exactly the failure this component exists to survive: in AGL-1541
 * the site-plugin gate stayed suspended, so nothing below the root ever
 * committed and every rAF-starved visitor went uncounted — 90 s on `/pricing`
 * with zero beacon activity. React will not commit a tree while a sibling is
 * suspended, and the only boundary that would isolate one is the page-wide
 * Suspense AGL-1541 had to delete. So the call is made during render, the way
 * `ErrorBeacon` (AGL-1538) installs at module scope for the same reason: the
 * thing that reports the failure cannot be scheduled by the thing that failed.
 *
 * Safe to do here because it is idempotent (the `beaconed` guard), takes no
 * part in the render output, and is client-only — the server must render
 * identically for every visitor or ISR would cache one of them.
 */
function sendPageviewBeacon(hostId: string | undefined, screenId?: string) {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return
  if (!hostId) return
  const key = `${hostId}\x00${window.location.pathname}`
  if (beaconed.has(key)) return
  beaconed.add(key)
  // The environment and internal-traffic gate, AFTER the once-per-pageview
  // guard so it is evaluated once, and BEFORE `claimDailyVisit` so an
  // uncounted pageview does not spend the tab's visit claim for the day.
  if (!analyticsBeaconMaySend()) return
  try {
    // Campaign attribution (AGL-1844): the three utm labels, straight off
    // the query string. Marketer-chosen labels on the URL the visitor is
    // already on — no cookie, no identifier; the collector clamps and caps
    // their cardinality server-side.
    const params = new URLSearchParams(window.location.search)
    sendAnalyticsBeacon({
      hostId,
      path: window.location.pathname,
      // Per-screen attribution (AGL-151).
      screenId: screenId || undefined,
      // External referrer host only; same-site moves are dropped
      // server-side (AGL-138).
      referrer: document.referrer || undefined,
      utmSource: params.get('utm_source') || undefined,
      utmMedium: params.get('utm_medium') || undefined,
      utmCampaign: params.get('utm_campaign') || undefined,
      // Visitor approximation (AGL-1844): true on the first pageview
      // this tab sends today. A day string in sessionStorage is the
      // entire mechanism — see `visit-claim.ts` for the honesty
      // contract. Claimed only when a beacon is actually sent (the
      // `beaconed` and gate guards above), so the claim and the pageview
      // travel together.
      newVisit:
        claimDailyVisit(new Date().toISOString().slice(0, 10)) || undefined,
    })
  } catch {
    // Analytics never breaks the page.
  }
}

/**
 * Screens this page load has already armed a dwell timer for. Module
 * scope, for the same reason as `beaconed`: a page remounts on its own,
 * and two timers for one screen would report the visit twice.
 */
const dwelling = new Set<string>()

/**
 * Report how long the visitor stayed on this screen (AGL-2182).
 *
 * `/product/analytics`'s per-screen mockup shows `Avg. time 2m 04s / on
 * this screen`, and nothing in the pipeline recorded duration of any kind.
 *
 * ## Why `pagehide` and not `beforeunload` or `visibilitychange`
 *
 * `beforeunload` disqualifies the page from the bfcache in every modern
 * browser and does not fire at all on mobile Safari — the platform where
 * most reading happens. `visibilitychange` fires on every tab switch, so
 * it would report a dozen fragments per visit and count each as a sample.
 * `pagehide` fires on both a real unload and a bfcache freeze, once.
 *
 * ## Why this is fire-and-forget, and why it degrades to nothing
 *
 * `sendBeacon` is queued by the browser and outlives the document; there
 * is no response to read and none is wanted. If it never fires — a killed
 * tab, a blocked beacon — the screen simply has fewer samples, and the
 * console renders no average rather than a wrong one. The failure mode is
 * silence, not a false number.
 *
 * Installed during render like the pageview beacon, and for the same
 * reason (AGL-1550): an effect only runs if React commits, and a page that
 * renders but never commits is the exact failure this file exists to
 * survive.
 */
function armDwellBeacon(hostId: string | undefined, screenId?: string) {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return
  if (!hostId || !screenId) return
  const key = `${hostId}\x00${screenId}`
  if (dwelling.has(key)) return
  dwelling.add(key)
  // Same gate as the pageview, and checked before the listener is installed
  // rather than inside `report`: a dwell sample belongs to a pageview, so a
  // pageview that was not counted must not leave a timer armed for one.
  if (!analyticsBeaconMaySend()) return
  const startedAt = Date.now()
  let reported = false
  const report = () => {
    if (reported) return
    reported = true
    sendAnalyticsBeacon({
      hostId,
      screenId,
      // The collector clamps and floors this; the client's clock is
      // the visitor's, so nothing here is trusted as measured.
      dwellMs: Date.now() - startedAt,
    })
  }
  window.addEventListener('pagehide', report)
}

/**
 * Every measurement and consent surface a published tenant page owns: the
 * first-party pageview beacon (AGL-82/138/151), the analytics tags the site
 * configures (AGL-138/661, mounted through their vendor's adapter — see
 * `analytics-provider.ts`) and the visitor-consent machinery that gates them
 * (AGL-1498).
 *
 * ## Why this is its own component (AGL-1550)
 *
 * All three used to live inside `CatchAllClient`, i.e. BELOW
 * `use(sitePluginLoader.ensure(...))` — the site-plugin gate (AGL-417).
 * Nothing here needs a plugin, a canvas node or a MobX observable; they were
 * only there because that is where the page happened to be written. The
 * coupling was invisible until it cost something: AGL-1541 wedged the gate
 * (a page-wide Suspense boundary whose reveal and hydration retry both rode
 * `requestAnimationFrame`, which never fires in a hidden, occluded or
 * prerendered tab) and took BOTH analytics systems and the consent surface
 * down with it — silently, for every rAF-starved visitor, until a GA
 * verification pass went looking. `/pricing` measured zero beacon activity at
 * 90 s.
 *
 * That specific mechanism is fixed. This component is the reason it cannot
 * come back in a new form: mounted as a SIBLING of `CatchAllClient` from
 * `page.tsx`, a plugin chunk that is slow, wedged or outright rejected can no
 * longer decide whether a pageview is counted. `ErrorBeacon` (AGL-1538) is
 * the same move one level up — it sits outside every page boundary in the
 * root layout precisely so it still reports when a page stays suspended.
 *
 * The import list is the invariant, and `site-analytics-independence.spec.ts`
 * asserts it: no plugin loader, no `@aglyn/aglyn` barrel (which pulls the
 * plugin manager and the canvas in behind it), nothing that can suspend.
 * Re-nesting this under the gate has to break a spec, not just a habit.
 *
 * ## What has NOT changed
 *
 * - **Consent still gates the tag at the source (AGL-1498).**
 *   `analyticsAllowed` is the render condition on the `<Script>` elements, so
 *   without a granting recorded state the vendor's script never LOADS — it is
 *   not loaded and then suppressed. Hoisting moved where the gate is evaluated, never
 *   when: an ungranted visitor gets no script from a sibling exactly as they
 *   got none from a descendant.
 * - **The posture is host-configured and read per host.** `resolveConsentPosture`
 *   needs the host's `consent.mode` (geo-conditional vs opt-in-everywhere),
 *   so the host document travels here as a prop — the same object `page.tsx`
 *   already hands `CatchAllClient`, so Flight serializes it once and this
 *   costs no payload.
 * - **ISR (AGL-1498).** Evaluation stays client-side: `consent.ready` starts
 *   false, so the cached HTML never varies by region or consent state and the
 *   visitor's own state attaches after hydration.
 * - **Surfaces.** Published only. The console preview mounts `ConsentBannerUi`
 *   itself under its region simulator (`document-preview.component.tsx`), and
 *   the editor canvas renders neither — neither goes through this route, so
 *   the editor still gains no banner and fires no analytics.
 */
export interface SiteAnalyticsProps {
  /**
   * The resolved tenant host. Carries the analytics settings, the consent
   * posture configuration and `$id`, which is what the beacon and the
   * per-host consent record are keyed on.
   */
  host?: (VisitorConsentHost & { $id?: string }) | null
  /** Per-screen attribution for the beacon (AGL-151). */
  screenId?: string
  /**
   * The request's CSP nonce, stamped onto every inline boot and library this
   * component mounts — each analytics tag's pair and the advertising pairs
   * (AGL-2640).
   *
   * A prop rather than a read, because a client component has no request to
   * read and because Next's automatic stamping never reaches these elements:
   * they mount after hydration from the client head manager, which carries no
   * nonce. The console learned this the expensive way — its enforcing policy
   * refused the inline boots while the libraries beside them loaded.
   *
   * The tenant hands nothing down today. It sends no `script-src` at all
   * (AGL-1228: a per-request nonce cannot match ISR-cached bytes), so there is
   * no nonce to hand and nothing that refuses an unnonced script. The prop is
   * the seam for the day that changes: the page passes a value here and every
   * element below is covered, with no second retrofit of this file.
   */
  nonce?: string
  /**
   * Vendors this page loads on the analytics grant besides Google Analytics
   * (AGL-3698), from the enrichers' shared `consentAnalyticsVendors` prop — a
   * live chat set to load with the page. The banner names them.
   */
  analyticsVendors?: readonly string[]
}

export default function SiteAnalytics({
  host,
  screenId,
  nonce,
  analyticsVendors,
}: SiteAnalyticsProps): ReactElement {
  const hostId = host?.$id
  // Whether the site configures a tag any declared provider mounts — read from
  // the compiled declarations, so it is known during render, before any
  // adapter has loaded. Each setting is format-checked (AGL-138): an id lands
  // inside an inline script.
  const tagConfigured = hostConfiguresAnalyticsTag(host)
  // Visitor consent (AGL-1498). Evaluated CLIENT-SIDE only — these pages are
  // ISR-cached, so the HTML must never vary by region or consent state; the
  // server and first client render agree on "nothing yet", and the visitor's
  // resolved state (explicit choice, GPC, implied default, or a pending
  // prior-consent ask) attaches after hydration. The gate itself is
  // posture-independent: only a GRANTING recorded state loads the script.
  const consentRequired = hostConsentRequired(host)

  // Both side effects fire DURING RENDER, before anything can suspend — see
  // `sendPageviewBeacon` above for why an effect is the wrong scheduler for
  // work whose whole job is to survive a page that never commits. Each is
  // guarded to run once per pageview and neither touches the render output.
  //
  // The pageview beacon is exempt from the consent gate on its own merits: it
  // sets no cookie and stores no visitor identifier, so there is nothing to
  // consent to. The consent kick is the opposite — it is the machinery that
  // decides the gate below, started early so `/api/consent/region` goes out
  // even on a wedged page.
  sendPageviewBeacon(hostId, screenId)
  armDwellBeacon(hostId, screenId)
  primeVisitorConsent(hostId, host, consentRequired)
  // CTA and outbound link clicks (AGL-1562). Installed here, during render,
  // for the same reason as the two calls above — and installed at all here
  // rather than in the marketing runtime because that runtime lives BELOW the
  // plugin gate, which is the coupling this component exists to end. A
  // delegated listener is what reaches the links React never sees: rich-text
  // and Custom HTML anchors are written as raw DOM.
  //
  // NOT consent-gated here, and that is the gate working rather than missing:
  // `trackEvent` reaches `window.gtag`, and without a granting consent state
  // the scripts above never render, so gtag does not exist and every
  // classified click is dropped. `surface` labels this as a tenant published
  // site — and `site` is the ONLY value this param has ever carried. The docs
  // app does NOT install this listener and is not going to until the
  // `aglyn-docs` Vercel project can see `libs/` at all
  // (`sourceFilesOutsideRootDirectory: false` — docs/ANALYTICS.md decision 7),
  // so do not read a missing `docs` row as docs having no clicks: GA4 enhanced
  // measurement reports docs outbound clicks unclassified and unsurfaced.
  installLinkClickTracking({ surface: 'site' })
  // Real-user Core Web Vitals (AGL-1642), same shape as the click listener:
  // installed during render, once per page load, delivered to the resident
  // tag through its adapter so the consent gate above stays structural — a
  // visitor whose tag never loads produces nothing. The module holds early
  // metrics briefly because the tags load late by design (once the page is
  // idle here, after the adapter's chunk); see `web-vitals-rum.ts` for why that hold is not the forbidden
  // pre-consent queue. The library itself arrives as a lazy chunk, so the
  // surface being measured pays nothing on its critical path.
  installWebVitalsReporting({ surface: 'site' })
  // The vendor adapters, fetched only by a page whose site configures a tag,
  // and started here during render like the calls above. The fetch never
  // holds the render: until it lands the page simply has no tag to mount,
  // which is what a visitor who has not granted sees anyway. It is also not
  // the site-plugin gate — a chunk of its own, with nothing to wait on.
  if (tagConfigured && typeof window !== 'undefined' && analyticsMayEmit()) {
    void loadAnalyticsProviders(ANALYTICS_PROVIDER_LOADERS)
  }
  const providers = useSyncExternalStore(
    subscribeAnalyticsProviders,
    analyticsProviders,
    noProviders,
  )

  const consent = useVisitorConsent(hostId, host, consentRequired)
  const analyticsAllowed = consentRequired
    ? consent.ready && isAnalyticsAllowed(host, consent.stored)
    : true
  // Advertising storage (AGL-1649). Off unless the host turned the question
  // on AND this visitor explicitly answered yes to that category; every
  // other path — no record yet, a visitor merely defaulted into analytics by
  // the implied default, a decline, an opt-out, GPC, or a record written
  // before the category existed — leaves the three advertising signals
  // denied, exactly as AGL-1622 set them. Resolved client-side like the rest
  // of the gate, so the ISR-cached HTML carries neither snippet.
  const advertisingAllowed =
    consentRequired && advertisingGrantedByRecord(host, consent.stored)
  // Not before the page has loaded and gone idle (AGL-3581): a tag injected
  // during hydration is vendor long tasks stacked on React's own, and nothing
  // it records is better for arriving a second sooner. A render gate, not
  // `lazyOnload` — see `page-idle.ts` for why the consent teardown needs it
  // to be. The advertising pairs below read the SAME store, so both flip in
  // one render and the shared-library hand-off stays true (AGL-2681).
  const pageIsIdle = usePageIdle()
  // The tags render on exactly this condition. `analyticsMayEmit()` is the
  // half that needs nobody's click (AGL-2067): `next dev` and Vercel preview
  // builds resolve a site's settings exactly as production does, and without
  // it they reported as real visits.
  const mounts: readonly AnalyticsTagMount[] =
    pageIsIdle && tagConfigured && analyticsAllowed && analyticsMayEmit()
      ? providers.flatMap((provider) =>
          mountsOf(provider, host, {
            consentRequired,
            advertising: advertisingAllowed,
          }),
        )
      : NO_MOUNTS
  // The first render that mounts a tag is also the first render in which the
  // advertising tags may mount — so the tag is not in the document yet when
  // they look for a loader to share (AGL-2681). The libraries the mounted
  // pairs bring are handed to them, derived from the same list the JSX below
  // renders so the two cannot disagree.
  const libraryKey = mounts
    .map((mount) => mount.library ?? '')
    .filter(Boolean)
    .join('\n')
  const sharedLibraries = useMemo(
    () => (libraryKey ? Object.freeze(libraryKey.split('\n')) : undefined),
    [libraryKey],
  )

  // Carry the campaign across the domain hop (AGL-1731). The capture on
  // `app.aglyn.com` shipped correct and was fed nothing: no code anywhere put
  // a `utm_*` parameter on a console-bound link, so every signup arrived
  // indistinguishable and every ad would have been unmeasurable.
  //
  // Installed during render for the same AGL-1550 reason as the two calls
  // above. Installed UNCONDITIONALLY, and gated separately, because the two
  // tiers have different consent answers: reading the campaign off the live
  // URL at click time writes nothing to the visitor's device and so needs no
  // grant, while remembering the FIRST touch across a walk to `/pricing` is
  // `analytics_storage` and waits for one.
  //
  // The gate is the boolean the tag itself uses, handed down rather than
  // recomputed, so there is one consent gate and it cannot drift from itself.
  // `null` is passed deliberately while `consent.ready` is false: unresolved
  // is not denied, and `analyticsAllowed` above flattens the two into one
  // `false` because that is all the tag needs to know. This does need to know.
  const analyticsStorageAllowed = consentRequired
    ? consent.ready
      ? isAnalyticsAllowed(host, consent.stored)
      : null
    : true
  setUtmForwardingConsent(analyticsStorageAllowed)
  installUtmForwarding({ consoleOrigin: CONSOLE_ORIGIN })

  // Carry the campaign to the moment the visitor identifies themselves. The
  // UTM labels on the beacon above are a page-view label and go no further,
  // so on their own they cannot say which campaign produced a form, a lead, a
  // contact or a booking. `utm-touch.ts` remembers the arrival for the
  // attribution window and the conversion doors attach it.
  //
  // The SAME resolved boolean the tag and the console hop are gated on, handed
  // down rather than recomputed: remembering a touch across the walk from the
  // landing page to the form is `analytics_storage`, and there is one gate for
  // it on this page. `null` while unresolved is passed through deliberately —
  // unresolved is not denied, and the module does nothing in that window.
  //
  // A visitor who converts on the page they landed on needs none of this: the
  // labels are still on the address bar and the door reads them there, with
  // nothing written to the device and nothing to consent to.
  setUtmTouchConsent(analyticsStorageAllowed)
  // And to the first-touch capture (AGL-3289), where the page includes it —
  // only the platform's own sites do. Keeping the first visit across the walk
  // to the sign-up is the same `analytics_storage`, so it takes the same
  // resolved gate: held in memory while unresolved, written once granted,
  // erased on a refusal. A page without the capture keeps only the answer.
  setPageFirstTouchStorage(analyticsStorageAllowed)
  // A visit's ordered steps (AGL-3605), for the funnels a site defines. The id
  // that ties them together leaves the browser, so it takes the same resolved
  // analytics gate as the tag — `null` while unresolved records nothing and
  // forgets nothing — and the beacon's own environment gate. Only a site that
  // records journeys sends any. See `site-journey.ts`.
  configureSiteJourney({
    hostId,
    enabled:
      !hostRecordsJourneys(host as { funnelRecording?: unknown } | null) ||
      (typeof window !== 'undefined' && !analyticsBeaconMaySend())
        ? false
        : analyticsStorageAllowed,
    path: typeof window === 'undefined' ? null : window.location.pathname,
  })

  return (
    <>
      {/* The site's analytics tags (AGL-138/661/2486), each an inline boot
          and then its library, as the vendor's adapter builds them.

          THE CONSENT GATE (AGL-1498): `analyticsAllowed` is enforcement at
          the source — without a granting recorded state no tag LOADS, rather
          than loading and being suppressed. The banner below is UI over this
          condition, not the condition. This deliberately also silences the
          event mirrors in the marketing runtime (overlay/experiment events)
          until consent: no tag, no events, which is the honest behavior.

          What the gate CANNOT do is unload a tag (AGL-1608). Dropping these
          elements on a mid-pageview withdrawal does not unload the vendor's
          library, so `storeVisitorConsent` tells the resident tag through its
          adapter as well as sweeping the cookies. That runs on the tag that is
          ALREADY loaded; the render condition here is unchanged.

          Anything an adapter declares in front of its tag — a consent-mode
          default, the platform's own-traffic stamp on the platform's own
          property — is inside the boot it returns, and so inside this
          condition: a gated visitor gets no boot, no library and no request
          to the vendor. `consent-mode-default.spec.tsx` goes red if a change
          ever hoists any of it out.

          `next/script` renders for real, and Next stamps it with the CSP nonce
          from the request header that middleware sets, so it keeps working
          when AGL-523 flips the policy from report-only to enforcing. */}
      {mounts.map((mount) => (
        <Fragment key={mount.id}>
          <Script
            id={`${mount.id}-init`}
            strategy="afterInteractive"
            nonce={nonce}
          >
            {mount.boot}
          </Script>
          <Script
            id={`${mount.id}-src`}
            strategy="afterInteractive"
            nonce={nonce}
            src={mount.src}
          />
        </Fragment>
      ))}
      {/* Consent-gated ADVERTISING tags — Aglyn's own marketing
          site only, and nowhere else.

          The same structural enforcement as the analytics tags above,
          extended to channels no analytics tag covers. Until this existed the
          `advertising` category could only ever be expressed as consent
          signals handed to the analytics tag, so any other tag could not be
          consent-gated at all.

          Mounted UNCONDITIONALLY and gated inside, which is not the shape of
          the block above it and is deliberate: the component also owns the
          withdrawal path, and a visitor who turns advertising off from "Your
          Privacy Choices" has to stop being tracked in THAT pageview. By then
          the vendor library has executed, and React dropping a `<Script>` does
          not unload it (the AGL-1608 lesson) — so the teardown runs from the
          consent-changed event, which needs a listener that is still mounted
          when the answer is no.

          On a customer's site it mounts only the tags that site's OWNER
          configured on Setup → Tracking (AGL-3694), with their own account
          ids, under the same consent gate; a site that configured none gets
          no listener and no script. Our own tags are on no host but ours —
          the DPA §3.2 promise, that Aglyn does not "sell"/"share" Customer
          Personal Data for its own advertising, expressed in code. The
          console needs no clause of its own: it does not render this route
          at all. */}
      <AdvertisingTags
        host={host}
        stored={consent.stored}
        ready={consent.ready}
        nonce={nonce}
        sharedLibraries={sharedLibraries}
      />
      {/* Visitor consent surfaces (AGL-1498): only when the machinery is
          live — the tool is active AND the site uses a gated feature. A
          site with no analytics has nothing to ask, so its visitors see
          nothing at all. What renders depends on the resolved state: the
          prior-consent banner (opt-in posture, undecided), or the
          persistent "Your Privacy Choices" pill — which in the implied posture
          is the ONE opt-out surface, on every page, template-independent.
          `consent.ready` keeps all of it out of the server HTML (ISR) and
          the first client render. Published surface only here; the console
          preview mounts the same component under its region simulator, and
          the editor canvas renders none of it. */}
      {consentRequired && consent.ready && hostId ? (
        <ConsentBannerUi
          hostId={hostId}
          stored={consent.stored}
          posture={consent.posture}
          country={consent.country}
          advertising={hostAsksAboutAdvertising(host)}
          analyticsVendors={analyticsVendors}
        />
      ) : null}
    </>
  )
}
